import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

import { storage } from '@/configs/storage';
import { TabRoute } from '@/navigation/types/navigation';
import { MILLISECONDS_UNTIL_POST_IS_OLD } from '@/shared/constants/newPosts';
import { BADGE_STORE_KEY } from '@/shared/constants/store';
import { isEmptyOrNull } from '@/shared/utils/string';

export const BadgeKey = {
  Chat: 'chat'
} as const;

export type BadgeKey = (typeof BadgeKey)[keyof typeof BadgeKey];

export type Key = BadgeKey | TabRoute;

export type BadgeState = Record<
  Key,
  { value: string | number | undefined; color: string } | undefined
>;

const initialBadgeState: BadgeState = {
  [BadgeKey.Chat]: undefined,
  [TabRoute.Start]: undefined,
  [TabRoute.Radio]: undefined,
  [TabRoute.Region]: undefined,
  [TabRoute.Profile]: undefined
};

export type PostBadgeEntry = { id: string; readTimestamp: number };

export type BadgeStore = {
  badges: BadgeState;
  updateBadge: (
    key: Key,
    value: string | number | undefined,
    color?: string
  ) => void;
  readPostBadgeEntries: PostBadgeEntry[];
  expirePostBadgeEntries: () => void;
  markPostAsRead: (id?: string) => void;
  markPostsAsRead: (ids: (string | undefined)[]) => void;
  newPostBadgeEntriesChanged: boolean;
  updateNewPostBadgeEntriesChanged: (value: boolean) => void;
  reset: () => void;
};

export const useBadgeStore = create<BadgeStore>()(
  persist(
    (set, get) => ({
      badges: initialBadgeState,
      updateBadge: (
        key: Key,
        value: string | number | undefined,
        color: string = '#fff'
      ) => {
        const currentBadges = get().badges;
        set({ badges: { ...currentBadges, [key]: { value, color } } });
      },
      readPostBadgeEntries: [],
      expirePostBadgeEntries: () => {
        const currentEntries = get().readPostBadgeEntries;
        const deadline = Date.now() - MILLISECONDS_UNTIL_POST_IS_OLD;
        const newEntries = currentEntries.filter(
          (e) => e.readTimestamp > deadline
        );
        if (newEntries.length === currentEntries.length) return;
        set({
          readPostBadgeEntries: newEntries,
          newPostBadgeEntriesChanged: true
        });
      },
      markPostAsRead: (id?: string) => {
        if (isEmptyOrNull(id)) return;
        const timestamp = Date.now();
        const currentEntries = get().readPostBadgeEntries;
        if (currentEntries.some((e) => e.id === id)) return;
        set({
          readPostBadgeEntries: [
            ...currentEntries,
            { id: id, readTimestamp: timestamp }
          ],
          newPostBadgeEntriesChanged: true
        });
      },
      markPostsAsRead: (ids: (string | undefined)[]) => {
        const currentEntries = get().readPostBadgeEntries;
        const existingIds = new Set(currentEntries.map((e) => e.id));
        const timestamp = Date.now();
        const newEntries: PostBadgeEntry[] = [];
        for (const id of ids) {
          if (!id || existingIds.has(id)) continue;
          existingIds.add(id);
          newEntries.push({ id, readTimestamp: timestamp });
        }
        if (newEntries.length === 0) return;
        set({
          readPostBadgeEntries: [...currentEntries, ...newEntries],
          newPostBadgeEntriesChanged: true
        });
      },
      newPostBadgeEntriesChanged: false,
      updateNewPostBadgeEntriesChanged: (value: boolean) => {
        set({ ...get(), newPostBadgeEntriesChanged: value });
      },
      reset: () => set({ badges: initialBadgeState, readPostBadgeEntries: [] })
    }),
    {
      name: BADGE_STORE_KEY,
      storage: createJSONStorage(() => storage)
    }
  )
);
